import { useCallback, useEffect, useRef, useState } from "react";
import qs from "qs";
import { useNavigate } from "react-router";

import { TAB_QUERY_PARAM } from "shared/constants";
import { useDebounce, useTimeoutManager } from "shared/hooks";
import { SortBy } from "shared/types";
import { areJsonObjectsEqual, randomID } from "shared/utils";

import { AssociatedVehiclesState } from "pages/shared/analytics/associatedVehicles/types";

import { SEARCH_DEBOUNCE_MS } from "features/ui/SearchInput/constants";

import { QS_PARSE_ARRAY_LIMIT, useQuery } from "services/hooks";

import { BrowserEventEmitter } from "./BrowserEventEmitter";
import { DEFAULT_FILTER_BUILDER_STATE } from "./FilterBuilder/constants";
import { FilterGroupState, FilterRowState } from "./FilterBuilder/types";
import {
  addRowFilterGroupState,
  areFilterStatesEqual,
  getFiltersQuery,
  getSearchFilterQuery,
  getTopLevelRowFromFilterGroupState,
  isAdvancedFilterState,
  isDefaultAdvancedFilterState,
  mergeFilterGroupStates,
  mergeFilterQueryStrings,
  removeAttributesFromFilterGroupState,
  removeInvalidAttributes,
  removeInvalidQuickFilters,
  removeInvalidSortKeys,
  updateOrAddRowFilterGroupState,
} from "./FilterBuilder/utils";
import {
  ChartSettingsChangeHandler,
  FilterChangeProps,
  FilterOperator,
  FilterSortState,
  InternalFilterState,
  InternalFilterStateParams,
  OccursFilterState,
  UseFilterSortState,
  UseFilterSortStateProps,
} from "./types";
import {
  filtersToQuickFilters,
  getNewFilterSortState,
  getPageKeyWithVersion,
  getUpdatedQueryParams,
  isFilterRowComplete,
  persistToLocalStorage,
  quickFiltersToFilters,
  sanitizeChartSettings,
  useInitialStateValuesAndKeys,
} from "./utils";

export const useFilterSortState = ({
  pageKey,
  defaultSort,
  disableUsingQuery = false,
  disableUsingLocalStorage = false,
  defaultFilterValues,
  defaultAssociatedVehiclesState,
  defaultTab,
  schemaAttributes,
  filterableAttributes,
  sortableAttributes,
  onlyAdvancedFilters,
  staticFilters,
  searchFields,
}: UseFilterSortStateProps): UseFilterSortState => {
  const query = useQuery();
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, SEARCH_DEBOUNCE_MS);
  const navigate = useNavigate();

  const pageKeyWithVersion = getPageKeyWithVersion(pageKey);
  const eventEmitter = useRef(new BrowserEventEmitter());
  const createManagedTimeout = useTimeoutManager();

  const {
    queryKeys,
    initialized,
    setInitialized,
    isAdvancedFilterEditor,
    setIsAdvancedFilterEditor,
    filters,
    setFilters,
    quickFilters,
    setQuickFilters,
    sort,
    setSort,
    columns,
    setColumns,
    chartSettings,
    setChartSettings,
    relatedSignalEventsFilter,
    setRelatedSignalEventsFilter,
    associatedVehiclesState,
    setAssociatedVehiclesState,
  } = useInternalFilterState({
    pageKeyWithVersion,
    defaultSort,
    disableUsingQuery,
    disableUsingLocalStorage,
    defaultFilterValues,
    onlyAdvancedFilters,
    defaultAssociatedVehiclesState,
    // use the current query as a key to check new values
    // in the URL and update the state accordingly
    currentQuery: JSON.stringify(query),
    eventEmitter,
  });

  const { filtersKey, sortKey, columnsKey } = queryKeys;

  const currentTab = query[TAB_QUERY_PARAM]
    ? String(query[TAB_QUERY_PARAM])
    : defaultTab;

  const onFilterSortStateChange = useCallback(
    (next: FilterSortState) => {
      const currentQuery = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
        arrayLimit: QS_PARSE_ARRAY_LIMIT,
        // We need higher depth because of the relates filter (default is 5)
        depth: 10,
      });

      const data = getNewFilterSortState({
        disableUsingQuery,
        next,
        currentQuery,
        queryKeys,
      });

      if (!disableUsingLocalStorage) {
        persistToLocalStorage(data, pageKeyWithVersion);
      }

      if (data.isAdvancedEditor !== undefined)
        setIsAdvancedFilterEditor(data.isAdvancedEditor);

      if (data.quickFilters) setQuickFilters(data.quickFilters);

      if (data.sort) setSort(data.sort);

      if (data.filters) {
        setFilters(data.filters);
        if (isAdvancedFilterState(data.filters)) {
          setIsAdvancedFilterEditor(true);
        }
      }

      if (data.columns) setColumns(data.columns);

      if (data.chartSettings) setChartSettings(data.chartSettings);

      if (data.relatedSignalEventsFilter)
        setRelatedSignalEventsFilter(data.relatedSignalEventsFilter);

      if (data.associatedVehiclesState)
        setAssociatedVehiclesState(data.associatedVehiclesState);

      if (!disableUsingQuery) {
        navigate(
          { search: getUpdatedQueryParams(data, queryKeys, currentQuery) },
          { replace: true }
        );
      }
    },
    // we dont want to run this when filters, sort, columns change because we use them inside this callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      navigate,
      persistToLocalStorage,
      disableUsingQuery,
      filtersKey,
      sortKey,
      columnsKey,
      disableUsingLocalStorage,
    ]
  );

  const manageQuickFilterChange = ({
    key,
    op_id,
    values = [],
    ...otherProps
  }: FilterChangeProps) => {
    const quickFilterEntry = quickFilters.find((f) => f.attribute === key);
    const newQuickFilter: FilterRowState = {
      type: "row",
      id: `row-${randomID()}`,
      attribute: key,
      operator: op_id,
      values,
      ...otherProps,
    };
    if (!quickFilterEntry)
      return updateQuickFilters([...quickFilters, newQuickFilter]);

    const newFilters = quickFilters.map((f) =>
      f.attribute === key ? newQuickFilter : f
    );
    updateQuickFilters(newFilters);
  };

  const manageFilterChange = ({
    key,
    op_id,
    values = [],
    outsideChange = true,
    replaceAttribute = true,
    ...otherProps
  }: FilterChangeProps) => {
    if (
      !isAdvancedFilterEditor &&
      ![
        FilterOperator.OCCURS,
        FilterOperator.NOT_OCCURS,
        FilterOperator.EXISTS,
        FilterOperator.NOT_EXISTS,
      ].includes(op_id)
    ) {
      // clear for remaining filters from initialization
      updateFilters(DEFAULT_FILTER_BUILDER_STATE, undefined, outsideChange);

      return manageQuickFilterChange({
        key,
        op_id,
        values,
        outsideChange,
        ...otherProps,
      });
    }

    if (!filters) return;

    const childRow = getTopLevelRowFromFilterGroupState(key, filters);
    const currentValues = childRow?.values || [];
    const currentOperator = childRow?.operator;

    // Do not do anything when values or operator haven't changed.
    // Note: we just do naive equality check.
    // Note 2: the values can be the same but operator changed and we still have to trigger filtering!
    if (
      (currentValues === values ||
        JSON.stringify(currentValues) === JSON.stringify(values)) &&
      currentOperator === op_id
    ) {
      return;
    }

    // If values are empty, remove this key from filter.
    if (!values.length) {
      const updated = removeAttributesFromFilterGroupState(filters, [key]);

      return updateFilters(updated, undefined, outsideChange);
    }

    let existingFilters: FilterGroupState;

    if (isDefaultAdvancedFilterState(filters)) {
      if (quickFilters.filter((f) => isFilterRowComplete(f)).length > 0) {
        existingFilters = quickFiltersToFilters(quickFilters);
      } else {
        existingFilters = filters;
      }
    } else {
      existingFilters = filters;
    }

    setIsAdvancedFilterEditor(true);

    if (replaceAttribute) {
      const newFilters = updateOrAddRowFilterGroupState(existingFilters, {
        type: "row",
        id: `row-${new Date()}`,
        attribute: key,
        values,
        operator: op_id,
        ...otherProps,
      });

      return updateFilters(newFilters, undefined, outsideChange);
    } else {
      const newFilters = addRowFilterGroupState(existingFilters, {
        type: "row",
        id: `row-${new Date()}`,
        attribute: key,
        values,
        operator: op_id,
        ...otherProps,
      });

      return updateFilters(newFilters, undefined, outsideChange);
    }
  };

  const manageOnSortChange = (sort: SortBy) =>
    onFilterSortStateChange({
      sort,
    });

  const manageOnVisibleFMColumnChange = (columns: string[]) =>
    onFilterSortStateChange({
      columns,
    });

  /**
   * Manage changes to the settings of one chart.
   * The settings for that one chart are put into a PageChartSettingsState object
   * and sent to the onFilterSortStateChange() function to be merged with the rest
   * of the chart settings in the data set.
   * @param chartSettings
   * @param chartKey
   */
  const manageChartSettingsChange: ChartSettingsChangeHandler = (
    chartSettings,
    chartKey
  ) => {
    if (!currentTab) return;

    // The chartSettings block sent to onFilterSortStateChange() should be of type
    // PageChartSettingsState so we build up the right structure.
    onFilterSortStateChange({
      // On rare occasions, the chartSettings object can contain undefined optionIds.
      // sanitizeChartSettings() removes those individual chart settings.
      chartSettings: sanitizeChartSettings({
        [currentTab]: {
          [chartKey]: chartSettings,
        },
      }),
    });
  };

  const manageRelatedSignalEventsFilterChange = (
    relatedSignalEventsFilter: OccursFilterState
  ) =>
    onFilterSortStateChange({
      relatedSignalEventsFilter,
    });

  const manageAssociatedVehiclesStateChange = (
    associatedVehiclesState: AssociatedVehiclesState
  ) =>
    onFilterSortStateChange({
      associatedVehiclesState,
    });

  const updateQuickFilters = (updatedFilters: FilterRowState[]) => {
    onFilterSortStateChange({
      quickFilters: updatedFilters,
      // clearing out filters when we update quick filters
      // to handle initialization of the filter state from
      // url or default filters (supporting parsing to quick filters
      // from filter builder state)
      filters: DEFAULT_FILTER_BUILDER_STATE,
    });
  };

  const updateFilters = (
    updatedFilters: FilterGroupState,
    callback?: (filters: FilterGroupState) => void,
    outsideChange = true
  ) => {
    if (isAdvancedFilterEditor || isAdvancedFilterState(updatedFilters)) {
      onFilterSortStateChange({
        filters: updatedFilters,
        quickFilters: [],
        isAdvancedEditor: true,
      });
    } else {
      onFilterSortStateChange({
        filters: DEFAULT_FILTER_BUILDER_STATE,
        quickFilters: filtersToQuickFilters(updatedFilters),
        isAdvancedEditor: false,
      });
    }

    if (callback) {
      callback(updatedFilters);
    }

    if (outsideChange) {
      eventEmitter.current.emit("outsideChange", updatedFilters);
    }
  };

  const resetFilters = (fieldNames?: string[]) => {
    const updatedFilters =
      (fieldNames &&
        removeAttributesFromFilterGroupState(filters, fieldNames)) ||
      DEFAULT_FILTER_BUILDER_STATE;

    setInitialized(false);

    if (isAdvancedFilterEditor) {
      updateFilters(updatedFilters);
    } else {
      updateQuickFilters(filtersToQuickFilters(updatedFilters));
    }

    // Filters don't like to be told what to set them to after being mounted. This is a little hacky solution to reset the component
    // that should be good enough
    createManagedTimeout(() => setInitialized(true), 10);
  };

  const resetSort = () => {
    onFilterSortStateChange({
      sort: defaultSort,
    });
  };

  /* Reset ALL table state */
  const resetFilterSortState = () => {
    setSearchValue("");
    const defaultState = {
      sort: defaultSort,
      filters: defaultFilterValues,
      columns: [],
    };
    // We want to check if the filters are already reset, to avoid infinite loop in case we get 400 on default state request
    if (
      JSON.stringify({ sort, filters, columns, chartSettings }) !==
      JSON.stringify(defaultState)
    ) {
      setInitialized(false);
      onFilterSortStateChange({
        sort: defaultSort,
        filters: defaultFilterValues,
        columns: [],
      });
      // Filter don't like to be told what to set it to after being mounted. This is a little hacky solution to reset the component
      // that should be good enough
      createManagedTimeout(() => setInitialized(true), 0);
    }
  };

  const restoreDefaults = () => {
    const defaults = defaultFilterValues ?? DEFAULT_FILTER_BUILDER_STATE;
    if (!isAdvancedFilterEditor && isAdvancedFilterState(defaults)) {
      onFilterSortStateChange({
        isAdvancedEditor: true,
        quickFilters: [],
        filters: defaults,
      });
    } else if (!isAdvancedFilterEditor) {
      onFilterSortStateChange({
        isAdvancedEditor: false,
        quickFilters: filtersToQuickFilters(defaults),
        filters: DEFAULT_FILTER_BUILDER_STATE,
      });
    } else {
      onFilterSortStateChange({
        quickFilters: [],
        filters: defaults,
      });
    }
  };

  const toggleQuickFilters = () => {
    // if quick filters are active, convert them to advanced filters
    if (!isAdvancedFilterEditor) {
      const quickFiltersGroup = quickFiltersToFilters(quickFilters);
      onFilterSortStateChange({
        isAdvancedEditor: true,
        quickFilters: [],
        filters: quickFiltersGroup,
      });

      return;
    }

    // if we can not convert advanced filters to quick filters if
    // they include any nested filter groups (the toggle is disabled)
    if (isAdvancedFilterState(filters)) {
      return;
    }

    // if advanced filters are active convert them to quick filters
    // and merge them with the "half finished" quick filters
    const newQuickFilters = filtersToQuickFilters(filters);
    const newAttributes = newQuickFilters.map((f) => f.attribute);

    onFilterSortStateChange({
      isAdvancedEditor: false,
      quickFilters: [
        ...newQuickFilters,
        // filter out unfinished quick filters that are now converted from advanced filters
        ...quickFilters.filter((f) => !newAttributes.includes(f.attribute)),
      ],
      filters: DEFAULT_FILTER_BUILDER_STATE,
    });

    return;
  };

  /**
   * When the user comes to the page initially we check if there's query params in the URL and store them to localStorage.
   * If there are none we check the localStorage and set filters / sort / columns based on that.
   * Stripping of attributes that are no longer valid is handled by the
   * reconciliation effect below.
   */
  useEffect(() => {
    const isAdvancedFilterEditorState = isAdvancedFilterState(filters);
    // Upon load make sure our advanced filter editor is set correctly based on the filters.
    setIsAdvancedFilterEditor(
      isAdvancedFilterState(filters) ? true : isAdvancedFilterEditor
    );

    onFilterSortStateChange({
      sort,
      columns,
      filters,
      quickFilters: isAdvancedFilterEditorState ? [] : quickFilters,
      chartSettings,
      relatedSignalEventsFilter,
      isAdvancedEditor: isAdvancedFilterEditor,
      associatedVehiclesState,
    });

    createManagedTimeout(() => setInitialized(true), 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterableSet = filterableAttributes ?? schemaAttributes;
  const filterableSetKey = filterableSet?.join(" ");
  const sortableSetKey = sortableAttributes?.join(" ");

  // After stripping invalid sort keys, fall back to defaultSort when nothing
  // valid remains (matching resetSort), validating defaultSort in case it too
  // points at a now-invalid column.
  const resolveReconciledSort = (cleanedSort: SortBy): SortBy => {
    if (Object.keys(cleanedSort).length > 0) return cleanedSort;

    if (!defaultSort || !sortableAttributes) return {};

    const cleanedDefault = removeInvalidSortKeys(
      defaultSort,
      sortableAttributes
    );

    return cleanedDefault ?? defaultSort;
  };

  /**
   * Strip persisted filters/sorts that reference an attribute which no longer
   * exists or was toggled not-filterable / not-sortable (e.g. an admin disables
   * attachments). Runs whenever the capability lists change — including when the
   * schema resolves after mount, or an attribute is toggled while the page is
   * open. Purely subtractive, so re-running is a no-op once the state is clean.
   */
  useEffect(() => {
    const hasFilterable = Boolean(filterableSet && filterableSet.length);
    const hasSortable = Boolean(
      sortableAttributes && sortableAttributes.length
    );
    if (!hasFilterable && !hasSortable) return;

    const cleanedFilters = hasFilterable
      ? removeInvalidAttributes(filters, filterableSet)
      : null;
    const cleanedQuickFilters = hasFilterable
      ? removeInvalidQuickFilters(quickFilters, filterableSet)
      : null;
    const cleanedSort =
      hasSortable && sortableAttributes
        ? removeInvalidSortKeys(sort, sortableAttributes)
        : undefined;

    if (!cleanedFilters && !cleanedQuickFilters && cleanedSort === undefined) {
      return;
    }

    onFilterSortStateChange({
      ...(cleanedFilters ? { filters: cleanedFilters } : {}),
      ...(cleanedQuickFilters ? { quickFilters: cleanedQuickFilters } : {}),
      ...(cleanedSort !== undefined
        ? { sort: resolveReconciledSort(cleanedSort) }
        : {}),
    });
    // Intentionally keyed only on the capability lists — the effect reads the
    // latest filters/sort from closure and is a no-op when they're already clean.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterableSetKey, sortableSetKey]);

  const setIsAdvancedFilterEditorInternal = (
    isAdvanced: boolean,
    clearQuickFilters = false
  ) => {
    if (clearQuickFilters) {
      onFilterSortStateChange({
        isAdvancedEditor: isAdvanced,
        quickFilters: [],
        filters,
      });
    } else {
      onFilterSortStateChange({
        isAdvancedEditor: isAdvanced,
      });
    }
  };

  const filtersWithStaticFilters = mergeFilterGroupStates(
    filters,
    staticFilters
  );
  const mergedFilters = mergeFiltersAndQuickFilters(
    filtersWithStaticFilters,
    quickFilters
  );
  const searchQuery = getSearchFilterQuery(
    searchFields ?? [],
    searchValue ? debouncedSearchValue : ""
  );
  const filtersQuery = searchQuery
    ? mergeFilterQueryStrings(getFiltersQuery(mergedFilters), searchQuery)
    : getFiltersQuery(mergedFilters);

  return {
    initialized,
    sort,
    manageOnSortChange,
    advancedFilters: filters,
    manageFilterChange,
    quickFilters,
    updateQuickFilters,
    filters: mergedFilters,
    visibleFilters: filters,
    resetFilters,
    resetSort,
    resetFilterSortState,
    updateFilters,
    failureModeColumns: columns,
    manageOnVisibleFMColumnChange,
    chartSettings,
    manageChartSettingsChange,
    relatedSignalEventsFilter,
    manageRelatedSignalEventsFilterChange,
    isAdvancedFilterEditor,
    setIsAdvancedFilterEditor: setIsAdvancedFilterEditorInternal,
    toggleQuickFilters,
    restoreDefaults,
    associatedVehiclesState,
    manageAssociatedVehiclesStateChange,
    eventEmitter,
    defaultFilters: defaultFilterValues,
    staticFilters,
    search: searchFields
      ? { value: searchValue, onChange: setSearchValue }
      : undefined,
    filtersQuery,
  };
};

/**
 * Internal hook to manage all state variables related to page filtering, sorting, and other UI configurations.
 *
 * This hook is separated from the main useFilterSortState hook to:
 * 1. Manage all individual state variables (filters, sort, columns, chart settings, etc.) and their setters.
 * 2. Provide a clear separation between state management and the logic for persisting/retrieving these states.
 * 3. Allow for easier testing and potential reuse of this state management logic.
 *
 * It initializes all state variables with their default or persisted values and returns both the current states
 * and their setter functions. This centralized state management approach allows the main usePageStateManager
 * hook to focus on the logic of persisting these states and handling user interactions.
 */
const useInternalFilterState = ({
  pageKeyWithVersion,
  defaultSort,
  disableUsingQuery = false,
  disableUsingLocalStorage = false,
  defaultFilterValues,
  onlyAdvancedFilters,
  defaultAssociatedVehiclesState,
  currentQuery,
  eventEmitter,
}: InternalFilterStateParams): InternalFilterState => {
  // currently initialized is the same for ALL filters on the page - if one is not initialized,
  // all are considered not. We should either remove this state or reconsider its implementation
  const [initialized, setInitialized] = useState(false);

  const { initialValues, queryKeys } = useInitialStateValuesAndKeys(
    pageKeyWithVersion,
    disableUsingQuery,
    disableUsingLocalStorage,
    defaultFilterValues
  );

  const defaultFilters: FilterGroupState =
    initialValues.filters || DEFAULT_FILTER_BUILDER_STATE;

  const [isAdvancedFilterEditor, setIsAdvancedFilterEditor] = useState(
    onlyAdvancedFilters
      ? onlyAdvancedFilters
      : defaultFilterValues && isAdvancedFilterState(defaultFilterValues)
        ? true
        : initialValues.isAdvancedEditor || false
  );

  const [filters, setFilters] = useState<FilterGroupState>(defaultFilters);

  const [quickFilters, setQuickFilters] = useState<FilterRowState[]>(
    isAdvancedFilterEditor
      ? []
      : (initialValues.quickFilters ??
          filtersToQuickFilters(
            defaultFilterValues ?? DEFAULT_FILTER_BUILDER_STATE
          ))
  );

  const [sort, setSort] = useState<SortBy | undefined>(
    initialValues.sort !== undefined ? initialValues.sort : defaultSort
  );

  const [columns, setColumns] = useState<string[]>(initialValues.columns || []);

  const [chartSettings, setChartSettings] = useState<
    UseFilterSortState["chartSettings"]
  >(initialValues.chartSettings);

  const [relatedSignalEventsFilter, setRelatedSignalEventsFilter] = useState<
    OccursFilterState | undefined
  >(initialValues.relatedSignalEventsFilter);

  const [associatedVehiclesState, setAssociatedVehiclesState] = useState<
    FilterSortState["associatedVehiclesState"]
  >(initialValues.associatedVehiclesState ?? defaultAssociatedVehiclesState);

  // If the URL changes from the outside of the filter state, we need to update the state
  // accordingly. We only want to do this when the url search query string
  // is different from the current state.
  useEffect(() => {
    if (!areFilterStatesEqual(defaultFilters, filters)) {
      if (isAdvancedFilterState(defaultFilters) && !isAdvancedFilterEditor) {
        setIsAdvancedFilterEditor(true);
        setFilters(defaultFilters);
        setQuickFilters([]);
      } else if (isAdvancedFilterEditor) {
        setFilters(defaultFilters);
        setQuickFilters([]);
      } else {
        // if we are not in advanced filter editor, we need to convert default filters to quick filters
        const newQuickFilters = filtersToQuickFilters(defaultFilters);
        setQuickFilters(newQuickFilters);
        setFilters(DEFAULT_FILTER_BUILDER_STATE);
      }

      // emit an event to notify page level FilterBuilder component of change
      eventEmitter?.current.emit("outsideChange", defaultFilters);
    }

    if (!areJsonObjectsEqual(initialValues.chartSettings, chartSettings)) {
      setChartSettings(initialValues.chartSettings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuery]);

  return {
    queryKeys,
    initialized,
    setInitialized,
    isAdvancedFilterEditor,
    setIsAdvancedFilterEditor,
    filters,
    setFilters,
    quickFilters,
    setQuickFilters,
    sort,
    setSort,
    columns,
    setColumns,
    chartSettings,
    setChartSettings,
    relatedSignalEventsFilter,
    setRelatedSignalEventsFilter,
    associatedVehiclesState,
    setAssociatedVehiclesState,
  };
};

const mergeFiltersAndQuickFilters = (
  filters: FilterGroupState,
  quickFilters: FilterRowState[]
) => {
  const isFilterRowValid = (f: FilterRowState) =>
    Boolean(f.attribute && f.operator && f.values && f.values.length > 0);

  // Create a Set to track unique combinations
  const uniqueCombinations = new Set<string>();

  // First, add all valid filter rows from the existing filters
  const addFilterRowsToSet = (group: FilterGroupState) => {
    group.children.forEach((child) => {
      if (child.type === "row") {
        const row = child as FilterRowState;
        const key = `${row.attribute}|${row.operator}|${JSON.stringify(row.values?.sort())}`;
        uniqueCombinations.add(key);
      } else if (child.type === "group") {
        addFilterRowsToSet(child as FilterGroupState);
      }
    });
  };

  addFilterRowsToSet(filters);

  // Then filter quickFilters to only include those not already in filters
  const uniqueQuickFilters = quickFilters.filter((f) => {
    if (!isFilterRowValid(f)) return false;

    const key = `${f.attribute}|${f.operator}|${JSON.stringify(f.values?.sort()) ?? ""}`;

    // If we've seen this combination before, filter it out
    if (uniqueCombinations.has(key)) return false;

    // Otherwise, add it to our set and keep it
    uniqueCombinations.add(key);

    return true;
  });

  const quickFiltersGroup: FilterGroupState = {
    type: "group",
    id: "group-quick-filters",
    anyAll: "all",
    children: uniqueQuickFilters,
  };

  return mergeFilterGroupStates(filters, quickFiltersGroup);
};
